import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { AttachmentDTO, CampaignDTO } from "@easyshop/shared";
import { useSearchParams } from "react-router-dom";
import { Button, Card, Drawer, DrawerHeader, Empty, ErrorState, FieldLabel, Loading, SegPills, cx, inputClass } from "@/components/ui";
import { PromoInsights } from "@/components/promos/PromoInsights";
import { DropZone, TargetPicker, initialTarget, toTarget, type TargetState } from "@/components/ComposeDrawer";
import { api } from "@/lib/api";
import { uploadFiles, useCampaign, useCampaigns, useGroups } from "@/lib/queries";
import { useSession } from "@/hooks/useSession";
import { dateFR, eur } from "@/lib/format";

const WEEKDAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const STATUS_LABELS: Record<CampaignDTO["status"], string> = { PLANNED: "Planifiée", ACTIVE: "Active", DONE: "Terminée" };

/** Jour local (minuit) d'une date ISO. */
function dayStart(iso: string | Date) {
  const d = new Date(iso);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

const rangeLabel = (c: CampaignDTO) => `${dateFR(c.startsAt)} → ${dateFR(c.endsAt)}`;

type FormState = { mode: "create" } | { mode: "edit"; campaign: CampaignDTO };

/**
 * « Promos & campagnes » : calendrier mensuel, liste, détail de la campagne
 * sélectionnée. Avec `boutiqueId`, seulement les campagnes de cette boutique
 * (onglet Planning de la fiche boutique).
 */
export function PromosPage({ boutiqueId }: { boutiqueId?: string } = {}) {
  const [params, setParams] = useSearchParams();
  const view = params.get("analyse") === "1" ? "analyse" : "calendrier";
  const setView = (v: string) => {
    const next = new URLSearchParams(params);
    if (v === "analyse") next.set("analyse", "1");
    else next.delete("analyse");
    setParams(next, { replace: true });
  };
  return (
    <div className="flex flex-col gap-4">
      <SegPills
        options={[
          { key: "calendrier", label: "Calendrier" },
          { key: "analyse", label: "Analyse des promos" },
        ]}
        value={view}
        onChange={setView}
        className="self-start border border-line bg-white"
      />
      {view === "analyse" ? <PromoInsights boutiqueIds={boutiqueId ? [boutiqueId] : undefined} /> : <PromosCalendar boutiqueId={boutiqueId} />}
    </div>
  );
}

function PromosCalendar({ boutiqueId }: { boutiqueId?: string }) {
  const { can } = useSession();
  const q = useCampaigns();
  const [monthOffset, setMonthOffset] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);

  const campaigns = useMemo(
    () => (q.data ?? []).filter((c) => !boutiqueId || c.boutiques.some((b) => b.id === boutiqueId)),
    [q.data, boutiqueId]
  );
  const today = new Date();
  const base = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const monthLabel = base.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  const firstDow = (base.getDay() + 6) % 7;
  const daysInMonth = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
  const todayKey = dayStart(today);

  const defaultSelected = campaigns.find((c) => c.status === "ACTIVE") ?? campaigns.find((c) => c.status === "PLANNED") ?? campaigns[0];
  const selected = campaigns.find((c) => c.id === selectedId) ?? defaultSelected;

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorState error={q.error} />;

  const cells: ({ day: number; time: number; promos: CampaignDTO[] } | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) {
    const t = new Date(base.getFullYear(), base.getMonth(), day).getTime();
    cells.push({ day, time: t, promos: campaigns.filter((c) => t >= dayStart(c.startsAt) && t <= dayStart(c.endsAt)) });
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-4">
        <Card className="min-w-0 flex-1 basis-[420px]" pad="px-4 py-[18px] sm:px-[26px] sm:py-[22px]">
          <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="text-[15px] font-bold capitalize">{monthLabel}</span>
            <div className="flex-1" />
            <button type="button" aria-label="Mois précédent" onClick={() => setMonthOffset((m) => m - 1)} className="rounded-[8px] px-[10px] py-1 text-[14px] hover:bg-page">
              ←
            </button>
            <button type="button" aria-label="Mois suivant" onClick={() => setMonthOffset((m) => m + 1)} className="rounded-[8px] px-[10px] py-1 text-[14px] hover:bg-page">
              →
            </button>
            {can("campaigns.manage") && (
              <button
                type="button"
                onClick={() => setForm({ mode: "create" })}
                className="ml-1 rounded-full bg-accent px-[14px] py-[7px] text-[12px] font-bold text-white hover:bg-accent-strong"
              >
                + Nouvelle campagne
              </button>
            )}
          </div>
          <div className="mb-[6px] grid grid-cols-7 gap-1 sm:gap-[6px]">
            {WEEKDAYS.map((w) => (
              <span key={w} className="block text-center text-[11px] font-semibold text-[oklch(0.6_0.01_150)]">
                {w}
              </span>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1 sm:gap-[6px]">
            {cells.map((cd, i) => {
              if (!cd) return <div key={`e${i}`} />;
              const isToday = cd.time === todayKey;
              const first = cd.promos.find((p) => p.id === selected?.id) ?? cd.promos[0];
              return (
                <div
                  key={cd.day}
                  onClick={first ? () => setSelectedId(first.id) : undefined}
                  className={cx(
                    "min-h-[60px] min-w-0 rounded-[10px] p-1 sm:p-[6px]",
                    isToday ? "border-[1.5px] border-positive bg-accent-row" : "border border-line-soft bg-white",
                    first && "cursor-pointer"
                  )}
                >
                  <span className="text-[11px] font-semibold text-[oklch(0.5_0.01_150)]">{cd.day}</span>
                  {first && (
                    <div
                      title={cd.promos.map((p) => p.name).join(", ")}
                      className={cx(
                        "mt-1 truncate rounded-[5px] px-[5px] py-[2px] text-[9.5px] font-bold",
                        first.id === selected?.id ? "bg-positive text-white" : "bg-positive-bg text-positive"
                      )}
                    >
                      {first.name}
                    </div>
                  )}
                  {cd.promos.length > 1 && <div className="mt-[2px] text-[9.5px] font-semibold text-muted-2">+{cd.promos.length - 1}</div>}
                </div>
              );
            })}
          </div>

          <div className="mt-[18px]">
            <div className="mb-[10px] text-[13px] font-bold">{boutiqueId ? "Promos de la boutique" : "Toutes les promos"}</div>
            {campaigns.length === 0 && <div className="py-2 text-[12.5px] text-muted">Aucune campagne pour le moment.</div>}
            {campaigns.map((c) => (
              <div
                key={c.id}
                onClick={() => {
                  setSelectedId(c.id);
                  const s = new Date(c.startsAt);
                  setMonthOffset((s.getFullYear() - today.getFullYear()) * 12 + s.getMonth() - today.getMonth());
                }}
                className={cx("flex cursor-pointer items-center gap-3 rounded-[10px] px-[6px] py-[10px]", c.id === selected?.id ? "bg-page" : "hover:bg-[oklch(0.985_0.004_145)]")}
              >
                <span className={cx("h-2 w-2 flex-shrink-0 rounded-full", c.status === "DONE" ? "bg-[oklch(0.75_0.01_150)]" : "bg-positive")} />
                <span className="min-w-0 flex-1 truncate text-[13px] font-semibold">{c.name}</span>
                {c.origin === "BOUTIQUE" && <OriginBadge c={c} />}
                <span className="flex-shrink-0 text-[11.5px] text-muted-2">{rangeLabel(c)}</span>
              </div>
            ))}
          </div>
        </Card>

        <div className="w-full flex-shrink-0 lg:w-[400px]">
          {selected ? (
            <CampaignDetail campaign={selected} onEdit={(c) => setForm({ mode: "edit", campaign: c })} onDeleted={() => setSelectedId(null)} />
          ) : (
            <Card>
              <Empty>
                Aucune campagne.
                {can("campaigns.manage") && (
                  <div className="mt-3">
                    <Button onClick={() => setForm({ mode: "create" })}>+ Nouvelle campagne</Button>
                  </div>
                )}
              </Empty>
            </Card>
          )}
        </div>
      </div>
      <Drawer open={form != null} onClose={() => setForm(null)} width={460}>
        {form && (
          <CampaignForm
            key={form.mode === "edit" ? form.campaign.id : "new"}
            campaign={form.mode === "edit" ? form.campaign : undefined}
            defaultBoutiqueId={boutiqueId}
            onClose={() => setForm(null)}
            onSaved={(c) => {
              setSelectedId(c.id);
              setForm(null);
            }}
          />
        )}
      </Drawer>
    </div>
  );
}

// --- Détail -----------------------------------------------------------------------------

export function CampaignDetail({ campaign, onEdit, onDeleted }: { campaign: CampaignDTO; onEdit: (c: CampaignDTO) => void; onDeleted: () => void }) {
  const qc = useQueryClient();
  const detail = useCampaign(campaign.id);
  const c = detail.data ?? campaign;
  const impact = detail.data?.impact;
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = async () => {
    if (!window.confirm(`Supprimer la campagne « ${c.name} » ?`)) return;
    setDeleting(true);
    setError(null);
    try {
      await api.del(`/campaigns/${c.id}`);
      onDeleted();
      await qc.invalidateQueries({ queryKey: ["campaigns"] });
      qc.removeQueries({ queryKey: ["campaign", c.id] });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Suppression impossible");
    } finally {
      setDeleting(false);
    }
  };

  const max = impact ? Math.max(1, impact.before, impact.during, impact.after) : 1;
  const bars = impact
    ? [
        { label: "Avant", value: impact.before, color: "oklch(0.75 0.01 150)", pending: false },
        { label: "Pendant", value: impact.during, color: "oklch(0.5 0.13 152)", pending: c.status === "PLANNED" },
        { label: "Après", value: impact.after, color: "oklch(0.6 0.05 155)", pending: c.status !== "DONE" && impact.after === 0 },
      ]
    : [];

  return (
    <Card pad="px-6 py-[22px]">
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 text-[15px] font-bold">{c.name}</span>
        {c.discountPct != null && (
          <span className="flex-shrink-0 rounded-full bg-accent px-[9px] py-[3px] text-[11px] font-bold text-white">-{c.discountPct} %</span>
        )}
      </div>
      <div className="mt-1 text-[12px] text-muted-2">
        {rangeLabel(c)} · {STATUS_LABELS[c.status]}
      </div>
      <div className="mt-2">
        <OriginBadge c={c} />
        {c.createdBy && <span className="ml-2 text-[11.5px] text-faint">par {c.createdBy}</span>}
      </div>
      {c.description ? (
        <div className="mt-3 whitespace-pre-line rounded-card-sm bg-accent-soft px-4 py-3 text-[13px] leading-[1.5] text-[oklch(0.25_0.02_150)]">
          {c.description}
        </div>
      ) : (
        <div className="mt-3 text-[12.5px] text-faint">Aucune description.</div>
      )}

      {c.productNames.length > 0 && (
        <div className="mt-[14px]">
          <div className="mb-[6px] text-[11.5px] font-bold text-muted-2">PRODUITS</div>
          {c.productNames.map((p) => (
            <div key={p} className="py-1 text-[13px]">
              {p}
            </div>
          ))}
        </div>
      )}

      <div className="mt-[14px]">
        <div className="mb-[6px] text-[11.5px] font-bold text-muted-2">BOUTIQUES CONCERNÉES ({c.boutiques.length})</div>
        <div className="flex flex-wrap gap-[6px]">
          {c.boutiques.map((b) => (
            <span key={b.id} className="rounded-full bg-page px-[9px] py-[3px] text-[11px] font-semibold">
              {b.city}
            </span>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <div className="mb-[6px] text-[11.5px] font-bold text-muted-2">FICHIERS ({c.attachments.length})</div>
        {c.attachments.length === 0 ? (
          <div className="text-[12px] text-faint">Aucun fichier joint.</div>
        ) : (
          <FileGallery files={c.attachments} />
        )}
      </div>

      {c.tasksCount > 0 && (
        <div className="mt-[14px] text-[11.5px] text-accent">
          Tâches créées automatiquement : affiche + mise en place dans {c.boutiques.length} boutique{c.boutiques.length > 1 ? "s" : ""}.
        </div>
      )}

      <div className="mt-[18px]">
        <div className="mb-2 text-[11.5px] font-bold text-muted-2">IMPACT VENTES</div>
        {detail.isLoading && <div className="text-[12px] text-muted">Chargement…</div>}
        {detail.error && <div className="text-[12px] text-danger">Impact indisponible.</div>}
        {bars.map((b) => (
          <div key={b.label} className="mb-[10px]">
            <div className="flex justify-between text-[12px]">
              <span>{b.label}</span>
              <span className={cx("font-bold", b.pending && "font-semibold text-faint")}>{b.pending ? "à venir" : eur(b.value)}</span>
            </div>
            <div className="mt-[5px] h-[7px] overflow-hidden rounded-[3px] bg-track">
              <div className="h-full" style={{ width: `${Math.round((b.value / max) * 100)}%`, background: b.color }} />
            </div>
          </div>
        ))}
        {impact && <div className="text-[11px] text-faint">CA journalier moyen des boutiques concernées</div>}
      </div>

      {c.canEdit && (
        <div className="mt-4 flex flex-wrap items-center gap-4 border-t border-line-soft pt-[14px]">
          <button type="button" onClick={() => onEdit(c)} className="text-[12.5px] font-semibold text-accent">
            Modifier
          </button>
          <button type="button" onClick={() => void remove()} disabled={deleting} className="text-[12.5px] font-semibold text-danger disabled:opacity-50">
            {deleting ? "Suppression…" : "Supprimer la campagne"}
          </button>
          {error && <div className="w-full text-[12px] font-semibold text-danger">{error}</div>}
        </div>
      )}
    </Card>
  );
}

/** « Réseau » ou « Boutique » : qui a lancé la campagne. */
export function OriginBadge({ c }: { c: Pick<CampaignDTO, "origin"> }) {
  return c.origin === "BOUTIQUE" ? (
    <span className="rounded-full bg-franchise-bg px-[8px] py-[2px] text-[10.5px] font-bold text-franchise">Promo boutique</span>
  ) : (
    <span className="rounded-full bg-positive-bg px-[8px] py-[2px] text-[10.5px] font-bold text-positive">Promo réseau</span>
  );
}

/** Vignettes des images + liste des autres fichiers, chacun téléchargeable. */
export function FileGallery({ files, onRemove }: { files: AttachmentDTO[]; onRemove?: (id: string) => void }) {
  const images = files.filter((f) => f.mimeType.startsWith("image/"));
  const others = files.filter((f) => !f.mimeType.startsWith("image/"));
  return (
    <div className="flex flex-col gap-2">
      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {images.map((f) => (
            <div key={f.id} className="group relative">
              <a href={f.url} target="_blank" rel="noreferrer" title={f.fileName}>
                <img src={f.url} alt={f.fileName} className="aspect-square w-full rounded-[10px] border border-line object-cover" />
              </a>
              {onRemove && <RemoveButton name={f.fileName} onClick={() => onRemove(f.id)} className="absolute right-1 top-1" />}
            </div>
          ))}
        </div>
      )}
      {others.map((f) => (
        <div key={f.id} className="flex items-center gap-2 rounded-[10px] bg-page px-3 py-2 text-[12.5px]">
          <span aria-hidden>📄</span>
          <a href={f.url} download={f.fileName} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate font-semibold text-accent">
            {f.fileName}
          </a>
          <span className="flex-shrink-0 text-[11px] text-faint">{Math.max(1, Math.round(f.size / 1024))} Ko</span>
          {onRemove && <RemoveButton name={f.fileName} onClick={() => onRemove(f.id)} />}
        </div>
      ))}
    </div>
  );
}

function RemoveButton({ name, onClick, className }: { name: string; onClick: () => void; className?: string }) {
  return (
    <button
      type="button"
      aria-label={`Retirer ${name}`}
      onClick={onClick}
      className={cx("flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-[14px] text-muted-2 shadow hover:text-danger", className)}
    >
      ×
    </button>
  );
}

// --- Création / modification --------------------------------------------------------------

function isoDay(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function CampaignForm({
  campaign,
  defaultBoutiqueId,
  onClose,
  onSaved,
}: {
  campaign?: CampaignDTO;
  defaultBoutiqueId?: string;
  onClose: () => void;
  onSaved: (c: CampaignDTO) => void;
}) {
  const { me, isManagerView } = useSession();
  const groups = useGroups();
  const qc = useQueryClient();
  const today = new Date();
  const allIds = me.boutiques.map((b) => b.id);
  const [name, setName] = useState(campaign?.name ?? "");
  const [description, setDescription] = useState(campaign?.description ?? "");
  const [start, setStart] = useState(campaign ? isoDay(new Date(campaign.startsAt)) : isoDay(today));
  const [end, setEnd] = useState(
    campaign ? isoDay(new Date(campaign.endsAt)) : isoDay(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 7))
  );
  const [target, setTarget] = useState<TargetState>(() => {
    if (campaign) return initialTarget(campaign.boutiques.map((b) => b.id), allIds);
    if (defaultBoutiqueId) return initialTarget([defaultBoutiqueId], allIds);
    return { ...initialTarget(undefined, allIds), mode: "reseau" };
  });
  const [files, setFiles] = useState<File[]>([]);
  const [kept, setKept] = useState<AttachmentDTO[]>(campaign?.attachments ?? []);
  // Promo d'une boutique pour elle-même : pas de tâches automatiques par défaut.
  const [createTasks, setCreateTasks] = useState(!isManagerView);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** Boutiques visées, pour la modification (l'API attend une liste d'ids). */
  const targetIds = (t: TargetState): string[] =>
    t.mode === "reseau" ? allIds : t.mode === "groupe" ? (groups.data?.find((g) => g.id === t.groupId)?.boutiqueIds ?? []) : t.boutiqueIds;

  const submit = async () => {
    setError(null);
    const t = toTarget(target);
    if (typeof t === "string") return setError(t);
    if (!name.trim()) return setError("Titre requis");
    if (!start || !end) return setError("Dates requises");
    if (end < start) return setError("La fin doit suivre le début");
    setBusy(true);
    try {
      const uploaded = files.length ? (await uploadFiles(files)).map((a) => a.id) : [];
      const common = {
        name: name.trim(),
        description: description.trim() || null,
        startsAt: new Date(`${start}T00:00:00`).toISOString(),
        endsAt: new Date(`${end}T00:00:00`).toISOString(),
      };
      const saved = campaign
        ? await api.patch<CampaignDTO>(`/campaigns/${campaign.id}`, {
            ...common,
            boutiqueIds: targetIds(target),
            addAttachmentIds: uploaded,
            removeAttachmentIds: campaign.attachments.filter((a) => !kept.some((k) => k.id === a.id)).map((a) => a.id),
          })
        : await api.post<CampaignDTO>("/campaigns", { ...common, target: t, createTasks, attachmentIds: uploaded });
      await qc.invalidateQueries({ queryKey: ["campaigns"] });
      await qc.invalidateQueries({ queryKey: ["campaign", saved.id] });
      void qc.invalidateQueries({ queryKey: ["planning"] });
      if (!campaign && createTasks) for (const k of ["conversations", "messages", "tracking", "tasks"]) void qc.invalidateQueries({ queryKey: [k] });
      onSaved(saved);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="flex min-h-full flex-1 flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <DrawerHeader title={campaign ? "Modifier la campagne" : "Nouvelle campagne"} onClose={onClose} />
      <div>
        <FieldLabel>Titre</FieldLabel>
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} placeholder="Ex. Semaine du Miel" className={inputClass} />
      </div>
      <div>
        <FieldLabel>Description de l'offre</FieldLabel>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={5000}
          placeholder={"Ex. 1 produit au choix offert dès 30 € d'achat\nou : 6 g achetés = 1 g offert"}
          className={cx(inputClass, "min-h-[110px] resize-y")}
        />
      </div>
      <div className="flex gap-[10px]">
        <div className="min-w-0 flex-1">
          <FieldLabel>Début</FieldLabel>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className={cx(inputClass, "px-[10px] py-[9px]")} />
        </div>
        <div className="min-w-0 flex-1">
          <FieldLabel>Fin</FieldLabel>
          <input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} className={cx(inputClass, "px-[10px] py-[9px]")} />
        </div>
      </div>

      <TargetPicker value={target} onChange={setTarget} label="Boutiques" />

      <div>
        <FieldLabel>Fichiers (visuels, affiches, PDF…)</FieldLabel>
        {kept.length > 0 && (
          <div className="mb-2">
            <FileGallery files={kept} onRemove={(id) => setKept((k) => k.filter((a) => a.id !== id))} />
          </div>
        )}
        <DropZone files={files} onFiles={setFiles} label="Glisser des fichiers ou cliquer pour en ajouter (images, PDF)" />
      </div>

      {!campaign && (
        <label className="flex cursor-pointer items-center gap-2">
          <input type="checkbox" checked={createTasks} onChange={(e) => setCreateTasks(e.target.checked)} className="h-4 w-4 accent-[oklch(0.4_0.08_155)]" />
          <span className="text-[12.5px]">Créer les tâches affiche + mise en place</span>
        </label>
      )}

      {error && <div className="rounded-[10px] bg-danger-bg px-3 py-2 text-[12.5px] font-semibold text-danger">{error}</div>}

      <div className="flex-1" />
      <div className="flex gap-[10px]">
        <button type="button" onClick={onClose} className="flex-1 rounded-full border border-line py-[11px] text-center text-[13px] font-bold">
          Annuler
        </button>
        <button
          type="submit"
          disabled={busy}
          className="flex-1 rounded-full bg-accent py-[11px] text-center text-[13px] font-bold text-white hover:bg-accent-strong disabled:opacity-60"
        >
          {busy ? "Enregistrement…" : campaign ? "Enregistrer" : "Créer la campagne"}
        </button>
      </div>
    </form>
  );
}
